import React from 'react';
import Axios from 'axios';
import { plato_api_find_exercise_result_for_grading_page_exercise_results_path } from 'modules/routes';
import { useQuery } from '@tanstack/react-query';
import { IQuestion, IQuestionAnswer } from '@/components/staffer/Grading/types';
import QuestionCard from '@/components/staffer/Grading/QuestionCards/QuestionCard';
import styles from '@/components/staffer/Grading/QuestionCards/questionCard.module.scss';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import { ScoreProgress } from '@/components/staffer/Grading/ScoreProgress';
import Text from '@/components/ui/Text';
import I18n from '@/modules/i18n';
import StatusIndicatorAndTimestamp from '@/components/staffer/Grading/StatusIndicatorAndTimestamp';
import { isAnswered, normalizedScore } from '@/components/staffer/Grading/utils';
import useSectionTitle from './useSectionTitle';

interface ModelQuestionsContainerProps {
  allModelQuestions: IQuestion[];
  isExpanded: boolean;
  modelId: string;
  modelType: string;
  questions: IQuestion[];
  questionAnswers: IQuestionAnswer[];
  toggleExpanded: (keys: string[]) => void;
}

const ModelQuestionsContainer = ({
  allModelQuestions,
  isExpanded,
  modelId,
  modelType,
  questionAnswers,
  questions,
  toggleExpanded,
}: ModelQuestionsContainerProps) => {
  const assignment = useGradingStore(state => state.assignment);
  const isQuestionScoringMode = assignment.scoring_mode === 'question';
  const student = useGradingStore(state => state.student);
  const nonPromptQuestions = allModelQuestions.filter(q => !['PageBreak', 'PromptOnly'].includes(q.type));
  const { sectionTitle } = useSectionTitle({
    exerciseId: parseInt(modelId, 10),
    exerciseType: modelType,
  });

  const turnedInQuery = useQuery([student.id, modelId], () => Axios.get(plato_api_find_exercise_result_for_grading_page_exercise_results_path({
    exercise_id: modelId,
    exercise_type: modelType,
    student_id: student.id,
  })));

  const isClassroomActivity = ['Actividad en el aula', 'Classroom Activity'].includes(assignment.assignment_details.type);
  const questionIds = questions.map(q => q.id);
  const numAnswered = questionAnswers?.filter(qa => questionIds.includes(qa.question_id) && isAnswered(qa)).length || 0;
  const scoreProgressTypes = isQuestionScoringMode
    ? ['ReadingGame', 'Notebook']
    : ['ReadingGame'];

  // Map questions to their answers for score calculations (excluding Prompt/PageBreak)
  const questionAnswersForQuestions = questions
    .filter(q => !['PageBreak', 'PromptOnly'].includes(q.type))
    .map(q => questionAnswers?.find(qa => qa.question_id.toString() === q.id.toString()));

  return (
    <div className={styles.card}>
      <div
        className={styles.modelQuestionsContainer}
        id={`${modelType}-${modelId}-content`}
        onClick={() => toggleExpanded([`${modelType}-${modelId}`])}
      >
        <div className="tw-w-1/2 tw-flex tw-items-center tw-pl-2 tw-box-border">
          <i className={`fa ${isExpanded ? 'fa-caret-down' : 'fa-caret-right'}`} />
          <Text weight="bold" xxl={{ size: 'lg' }}>
            {sectionTitle}
          </Text>
        </div>
        <div className="tw-w-1/2 tw-flex tw-items-center tw-box-border">
          {turnedInQuery.isSuccess && <StatusIndicatorAndTimestamp turnedInQueryData={turnedInQuery.data.data} />}
          <Text className={styles.questionsAnswered} size="sm" weight="bold" xxl={{ size: 'md' }}>
            {numAnswered}
            {' '}
            /
            {' '}
            {questions.filter(q => !['PageBreak', 'PromptOnly'].includes(q.type)).length}
            {' '}
            {I18n.t('answered').toLowerCase()}
          </Text>
          {(scoreProgressTypes.includes(modelType) || (isQuestionScoringMode && isClassroomActivity)) && (
            <ScoreProgress
              hasAnyScores={questionAnswersForQuestions.some(qa => qa?.score != null)}
              isTurnedIn={!!(turnedInQuery?.data?.data?.completed_at)}
              maxPoints={normalizedScore(assignment, questions
                .filter(q => !['PageBreak', 'PromptOnly'].includes(q.type))
                .reduce((acc, cur) => cur.points + acc, 0))}
              points={
                normalizedScore(
                  assignment,
                  questionAnswersForQuestions.reduce((acc, qa) => (
                    qa?.score != null ? acc + parseFloat(qa.score) : acc
                  ), 0),
                )}
            />
          )}
        </div>
      </div>
      {isExpanded
        && (
          <div className={styles.bodyWrapper}>
            {questions.map((q) => {
              const questionAnswer = questionAnswers?.find(qa => qa.question_id.toString() === q.id.toString());
              const position = nonPromptQuestions.findIndex(question => question.id === q.id);

              return (
                <QuestionCard
                  key={q.id}
                  position={position !== -1 ? position + 1 : undefined}
                  question={q}
                  questionAnswer={questionAnswer}
                />
              );
            })}
          </div>
        )
      }
    </div>
  );
};

export default ModelQuestionsContainer;
